'use client';
import { ChevronIcon } from '@/components/ui';
import { TypeListFields } from '@/core/generated-types';
import { shouldHideWithLocaleOverride } from '@/utils/localeOverride';
import { Entry } from 'contentful';
import { FC, Fragment, useState } from 'react';
import { UniversalLink, UniversalLinkFields } from '../UniversalLink';
import { HamburgerMenuItem } from './HamburgerMenu';
import HamburgerMenuLinks from './HamburgerMenuLinks';
import useAuth, { AuthState } from '@/context/AuthContext';
import interpolate from '@/utils/translations/interpolate';
import { VariationHamburgerMenuLink } from './VariationHamburgerMenuLink';
import {
  trackItemClickedEvent,
  CLICKED_ITEM_TYPES,
  PAGE_LOCATIONS,
  CTA_NAMES,
} from '@/utils/rudderstack';
import classNames from 'classnames';
import { MaybeRenderCoursePacksLink } from './MaybeRenderCoursePacksLink';

const LOGOUT_URL = `https://shop.${process.env.NEXT_PUBLIC_BACKEND_DOMAIN}/account/logout`;

export interface HamburgerMenuLinkProps {
  menuItem: HamburgerMenuItem;
  onMenuItemTap: () => void;
  indented?: boolean;
  logoutText?: string;
  playKitsByAgeLinks?: Entry<Record<string, any>>[];
  playKitsDropdownLinks?: Entry<Record<string, any>>[];
  shopAllPrimaryLinks?: Entry<Record<string, any>>[];
  shopAllAdditionalLinks?: Entry<Record<string, any>>[];
}

export interface MobileMenuItemProps {
  link: Entry<UniversalLinkFields>;
  onMenuItemTap: () => void;
  indented?: boolean;
  primaryDropdownLinks?: Entry<Record<string, any>>[];
  secondaryDropdownLinks?: Entry<Record<string, any>>[];
  headingText?: string;
}

export const MobileMenuItem: FC<MobileMenuItemProps> = ({
  link,
  indented,
  onMenuItemTap,
}) => {
  const badgeBgColor = link.fields.badgeBgColor?.fields.color;
  const badgeTextColor = link.fields.badgeTextColor?.fields.color;

  return (
    <li
      className={`flex w-full items-center ${indented ? 'ml-8 mt-4' : 'mt-8'}`}
      onClick={onMenuItemTap}
    >
      <UniversalLink link={link} classes="text-h3 font-bold w-full">
        {link.fields.badgeText && (
          <>
            {link.fields.linkText}
            <div
              className={classNames(
                'ml-6 rounded-full px-4 py-1 text-sm font-bold text-charcoal shadow-lg',
                {
                  'bg-white': !badgeBgColor,
                  [`bg-${badgeBgColor}`]: badgeBgColor,
                  'text-charcoal': !badgeTextColor,
                  [`text-${badgeTextColor}`]: badgeTextColor,
                },
              )}
            >
              {link.fields.badgeText}
            </div>
          </>
        )}
      </UniversalLink>
    </li>
  );
};

const MobileDropdownMenuItem: FC<MobileMenuItemProps> = ({
  link,
  onMenuItemTap,
  primaryDropdownLinks = [],
  secondaryDropdownLinks = [],
  headingText,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const handleLinkClick = (linkText: string) => {
    trackItemClickedEvent({
      type: CLICKED_ITEM_TYPES.LINK,
      cta: linkText as CTA_NAMES,
      pageLocation: PAGE_LOCATIONS.NAVIGATION,
      url: window.location.href,
    });
  };

  return (
    <li className={`flex w-full items-center mt-8`}>
      <div
        className="text-h3 font-bold w-full"
        onClick={() => setIsExpanded(!isExpanded)}
        data-testid="mobile-dropdown-menu-item"
      >
        <div className="flex items-center">
          {link.fields.linkText}
          <ChevronIcon classes="ml-2" direction="down" color="white" />
          {link.fields.badgeText && (
            <div className="ml-6 rounded-full bg-white px-4 py-1 text-sm font-bold text-charcoal shadow-lg">
              {link.fields.badgeText}
            </div>
          )}
        </div>
        {isExpanded && (
          <div className="px-8 pt-4">
            {headingText && (
              <h4 className="text-xl mb-2 font-bold">{headingText}</h4>
            )}
            <ul tabIndex={0}>
              {primaryDropdownLinks.map((link: any) => {
                return (
                  <li key={link.sys.id} onClick={onMenuItemTap}>
                    <UniversalLink
                      link={link}
                      classes="hover:text-blue-deep font-medium mt-3"
                      onClick={() => handleLinkClick(link.fields.linkText)}
                    />
                  </li>
                );
              })}
            </ul>
            <div className="border-t border-gray-200 my-4"></div>
            <ul tabIndex={0}>
              {secondaryDropdownLinks.map((link: any) => {
                return (
                  <li key={link.sys.id} onClick={onMenuItemTap}>
                    <UniversalLink
                      link={link}
                      classes="hover:text-blue-deep font-medium mt-3"
                      onClick={() => handleLinkClick(link.fields.linkText)}
                    />
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </div>
    </li>
  );
};

const interpolateStringWithUserName = (
  string: string | undefined,
  user: AuthState | null,
) => {
  if (!string) {
    return '';
  }

  return interpolate(string, { first_name: user?.attributes?.name || 'user' });
};

export const HamburgerMenuLink = (props: HamburgerMenuLinkProps) => {
  const {
    menuItem,
    onMenuItemTap,
    indented,
    logoutText,
    playKitsByAgeLinks,
    playKitsDropdownLinks,
    shopAllPrimaryLinks,
    shopAllAdditionalLinks,
  } = props;
  const [isExpanded, setIsExpanded] = useState(false);
  const { user, signOut } = useAuth();
  const onClickSignOut = async () => {
    await signOut();
    window.location.assign(LOGOUT_URL);
  };

  if (
    'localeOverride' in menuItem.fields &&
    shouldHideWithLocaleOverride(menuItem.fields.localeOverride)
  ) {
    return null;
  }

  const contentTypeId = menuItem.sys.contentType?.sys.id;
  switch (contentTypeId) {
    case 'launchDarklyVariationContainer':
      return <VariationHamburgerMenuLink {...props} />;
    case 'link_internal':
    // Falls through.
    case 'link_external':
      const link = menuItem as Entry<UniversalLinkFields>;
      const coursePacksLinkId = '3osNeMVeBNbc6w8lgHP2tG';
      const playKitsLinkId = '4QvVwvm2ldhwBKZ4tE1aKJ';
      const shopAllLinkId = 'FVodBxgM3SeobT4hkx3Ry';

      if (link?.sys.id === coursePacksLinkId) {
        return (
          <MaybeRenderCoursePacksLink
            key={link?.sys?.id}
            link={link}
            onMenuItemTap={onMenuItemTap}
            indented={!!indented}
            data-testid="course-packs-link"
          />
        );
      }

      if (link?.sys?.id === playKitsLinkId && playKitsByAgeLinks?.length) {
        return (
          <MobileDropdownMenuItem
            key={link?.sys.id}
            link={link}
            onMenuItemTap={onMenuItemTap}
            primaryDropdownLinks={playKitsByAgeLinks}
            secondaryDropdownLinks={playKitsDropdownLinks}
            data-testid="play-kits-link"
            headingText="Shop by age:"
          />
        );
      }

      if (
        link?.sys?.id === shopAllLinkId &&
        (shopAllPrimaryLinks?.length || shopAllAdditionalLinks?.length)
      ) {
        return (
          <MobileDropdownMenuItem
            key={link?.sys.id}
            link={link}
            onMenuItemTap={onMenuItemTap}
            primaryDropdownLinks={shopAllPrimaryLinks}
            secondaryDropdownLinks={shopAllAdditionalLinks}
            data-testid="shop-all-link"
          />
        );
      }

      return (
        <MobileMenuItem
          key={link?.sys.id}
          link={link}
          onMenuItemTap={onMenuItemTap}
          indented={!!indented}
        />
      );
    case 'list':
      const list = menuItem as Entry<TypeListFields>;
      const fields = list?.fields.listItems as HamburgerMenuItem[];
      return (
        <Fragment key={list?.sys.id}>
          <li
            className="mt-8 flex cursor-pointer items-center text-h3 font-bold"
            onClick={() => setIsExpanded(!isExpanded)}
          >
            {interpolateStringWithUserName(list.fields.title, user)}
            <ChevronIcon
              classes="ml-2"
              direction={isExpanded ? 'up' : 'down'}
              color="white"
            />
          </li>
          {isExpanded && (
            <>
              <HamburgerMenuLinks
                key={list?.sys.id}
                menu={fields}
                onMenuItemTap={onMenuItemTap}
                indented
              />
              {logoutText && (
                <button
                  className="ml-8 mt-4 flex w-full cursor-pointer flex-row items-center text-h3 font-bold"
                  onClick={onClickSignOut}
                >
                  {logoutText}
                </button>
              )}
            </>
          )}
        </Fragment>
      );
    default:
      console.warn(`Unhandled mobile menu content type: ${contentTypeId}`, {
        menuItem,
      });
      return null;
  }
};
